import { useEffect, useState } from 'react';
import { Button, Group, LoadingOverlay, Modal, Stack, TextInput } from '@mantine/core';
import type { Keyword, KeywordCreateRequest, KeywordUpdateRequest } from '@/types/keywords';
import styles from './ShortCodes.module.sass';
import { SaveErrorBox } from '@/components';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type ShortCodeModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (data: KeywordCreateRequest | KeywordUpdateRequest) => Promise<void> | void;
  keyword?: Keyword | null;
};

export const ShortCodeModal = ({ opened, onClose, onSave, keyword }: ShortCodeModalProps) => {
  const [keywordValue, setKeywordValue] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [keywordError, setKeywordError] = useState<string>('');
  const [messageError, setMessageError] = useState<string>('');
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (opened) {
      if (keyword) {
        setKeywordValue(keyword.keyword);
        setMessage(keyword.message);
      } else {
        setKeywordValue('');
        setMessage('');
      }
      setKeywordError('');
      setMessageError('');
    }
  }, [opened, keyword]);

  const handleCancel = () => {
    setKeywordValue('');
    setMessage('');
    setKeywordError('');
    setMessageError('');
    onClose();
  };

  const validateFields = (): boolean => {
    let isValid = true;

    if (!keywordValue.trim()) {
      setKeywordError('Keyword is required');
      isValid = false;
    } else {
      setKeywordError('');
    }

    if (!message.trim()) {
      setMessageError('Message is required');
      isValid = false;
    } else {
      setMessageError('');
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateFields()) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        keyword: keywordValue.trim(),
        message: message.trim(),
      });
      setKeywordValue('');
      setMessage('');
      setKeywordError('');
      setMessageError('');
    } catch (error) {
      console.error('Failed to save short code', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeywordChange = (value: string) => {
    setKeywordValue(value);
    if (keywordError) {
      setKeywordError('');
    }
  };

  const handleMessageChange = (value: string) => {
    setMessage(value);
    if (messageError) {
      setMessageError('');
    }
  };

  const isValid = keywordValue.trim().length > 0 && message.trim().length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Short Code"
      size="md"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <Stack pos="relative" mt="md">
        <LoadingOverlay visible={isSaving} />
        <TextInput
          label="Keyword"
          placeholder="Enter keyword"
          value={keywordValue}
          onChange={(e) => handleKeywordChange(e.target.value)}
          error={keywordError}
          required
        />
        <TextInput
          label="Message"
          placeholder="Enter message"
          value={message}
          onChange={(e) => handleMessageChange(e.target.value)}
          error={messageError}
          required
        />
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isSaving} loading={isSaving}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
